import React, {useState, useEffect} from "react";
import FullCalendar from "@fullcalendar/react";
import timelinePlugin from "@fullcalendar/timeline";
import interactionPlugin from "@fullcalendar/interaction";
import Swal from "sweetalert2";
import "./assets/css/EventEditor.css";
import Modal from 'react-bootstrap/Modal';
import UsersSearch from "../dashboard/calender/UsersSearch";
import {v4 as uuidv4} from "uuid";

const TimelineEditor = ({dutyEvent, onEventsChange}) => {
    const [events, setEvents] = useState([]);
    const [calendarKey, setCalendarKey] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);

    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedEventIndex, setSelectedEventIndex] = useState(null);

    useEffect(() => {
        if (dutyEvent) {
            setEvents([
                {
                    id: "1",
                    title: `${dutyEvent.date_time.slice(11, 16)} - ${dutyEvent.date_time_end.slice(11, 16)}`,
                    start: new Date(dutyEvent.date_time).toISOString(),
                    end: new Date(dutyEvent.date_time_end).toISOString(),
                    color: "blue",
                    extendedProps: {
                        my_duty: 1,
                        accept_user: null
                    }
                },
            ]);
            setCalendarKey(prev => prev + 1);
        }
    }, [dutyEvent]);

    useEffect(() => {
        onEventsChange?.(events);
        const licenseMessage = document.querySelector(".fc-license-message");
        if (licenseMessage) licenseMessage.classList.add("d-none");
    }, [events, onEventsChange]);

    const formatEventTitle = (start, end) => {
        if (!start || !end) return "Título inválido";
        const s = new Date(start).toLocaleTimeString("pt-BR", {hour: "2-digit", minute: "2-digit"});
        const e = new Date(end).toLocaleTimeString("pt-BR", {hour: "2-digit", minute: "2-digit"});
        return `${s} - ${e}`;
    };

    const handleEventResize = (info) => {
        const {id, start, end} = info.event;

        const updatedEvents = events.map((event, index, arr) => {
            if (event.id === id) {
                const prev = arr[index - 1];
                const next = arr[index + 1];

                if (prev && new Date(start) <= new Date(prev.start)) {
                    Swal.fire("Erro", "Não pode sobrepor o evento anterior.", "error");
                    return event;
                }

                if (next && new Date(end) >= new Date(next.end)) {
                    Swal.fire("Erro", "Não pode sobrepor o próximo evento.", "error");
                    return event;
                }

                return {
                    ...event,
                    start: start.toISOString(),
                    end: end.toISOString(),
                    title: formatEventTitle(start, end)
                };
            }
            return event;
        });

        setEvents(updatedEvents);
        setCalendarKey(prev => prev + 1);
    };

    const handleEventDelete = async (info) => {
        if (isDeleting) return;
        setIsDeleting(true);

        try {
            if (events.length > 1) {
                const event = info.event || info;
                const {id, start, end} = event;
                const idx = events.findIndex((e) => e.id === id);
                const prev = events[idx - 1];
                const next = events[idx + 1];
                const updatedEvents = events.filter(e => e.id !== id);

                if (updatedEvents.length === 1 && events.length === 2) {
                    const only = updatedEvents[0];
                    updatedEvents[0] = {
                        ...only,
                        start: new Date(Math.min(new Date(start), new Date(only.start))).toISOString(),
                        end: new Date(Math.max(new Date(end), new Date(only.end))).toISOString(),
                        title: formatEventTitle(start, end),
                    };
                } else if (prev) {
                    updatedEvents.forEach((e, i) => {
                        if (e.id === prev.id) {
                            updatedEvents[i] = {
                                ...e,
                                end: new Date(Math.max(new Date(end), new Date(prev.end))).toISOString(),
                                title: formatEventTitle(e.start, end),
                            };
                        }
                    });
                } else if (next) {
                    updatedEvents.forEach((e, i) => {
                        if (e.id === next.id) {
                            updatedEvents[i] = {
                                ...e,
                                start: new Date(Math.min(new Date(start), new Date(next.start))).toISOString(),
                                title: formatEventTitle(start, e.end),
                            };
                        }
                    });
                }

                setEvents(updatedEvents);
                setCalendarKey(prev => prev + 1);
            } else {
                await Swal.fire("Erro", "Apenas um plantão, não poderá ser excluído.", "error");
            }
        } finally {
            setTimeout(() => setIsDeleting(false), 300);
        }
    };

    const roundToQuarterHour = (date) => {
        const d = new Date(date);
        d.setMinutes(Math.round(d.getMinutes() / 15) * 15, 0, 0);
        return d;
    };

    const handleEventClick = (info) => {
        const {id, start, end, extendedProps} = info.event;
        const isDisclosed = extendedProps.my_duty === 0;
        const duration = new Date(end) - new Date(start);
        const rect = info.el.getBoundingClientRect();
        const clickX = info.jsEvent.clientX - rect.left;
        const ratio = clickX / rect.width;
        const clickTime = roundToQuarterHour(new Date(new Date(start).getTime() + duration * ratio));

        Swal.fire({
            title: "Escolha uma ação:",
            icon: "question",
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: `Cortar em ${clickTime.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit"
            })}`,
            cancelButtonText: isDisclosed ? "Atribuir a mim" : "Disponibilizar para outros",
            denyButtonText: "Excluir"
        }).then((res) => {
            if (res.isConfirmed) {
                const durBefore = (clickTime - new Date(start)) / 3600000;
                const durAfter = (new Date(end) - clickTime) / 3600000;
                if (durBefore < 3 || durAfter < 3) {
                    Swal.fire("Erro", "O corte deixaria uma das partes com menos de 3 horas.", "error");
                    return;
                }

                const updated = events.flatMap(e =>
                    e.id === id
                        ? [
                            {...e, end: clickTime.toISOString(), title: formatEventTitle(start, clickTime)},
                            {
                                id: uuidv4(),
                                start: clickTime.toISOString(),
                                end: end,
                                title: formatEventTitle(clickTime, end),
                                color: "blue",
                                extendedProps: {my_duty: 1, accept_user: null}
                            }
                        ]
                        : e
                );
                setEvents(updated);
                setCalendarKey(prev => prev + 1);
            } else if (res.dismiss === Swal.DismissReason.cancel) {
                setEvents(events.map(e =>
                    e.id === id
                        ? {
                            ...e,
                            color: isDisclosed ? "blue" : "gray",
                            extendedProps: {...e.extendedProps, my_duty: isDisclosed ? 1 : 0}
                        }
                        : e
                ));
                setCalendarKey(prev => prev + 1);
            } else if (res.isDenied) {
                handleEventDelete(info);
            }
        });
    };

    const handleSplitEvent = (parts) => {
        if (events.length !== 1) return;
        const event = events[0];
        const startTime = new Date(event.start).getTime();
        const endTime = new Date(event.end).getTime();
        const totalDuration = (endTime - startTime) / 3600000;
        const minPerPart = totalDuration / parts;

        if (minPerPart < 3) {
            Swal.fire("Erro", `Cada parte deve ter no mínimo 3 horas. Total: ${totalDuration.toFixed(1)}h`, "error");
            return;
        }

        const interval = (endTime - startTime) / parts;
        const newEvents = [];

        for (let i = 0; i < parts; i++) {
            const partStart = new Date(startTime + i * interval);
            const partEnd = new Date(startTime + (i + 1) * interval);
            if (partStart.getTime() !== partEnd.getTime()) {
                newEvents.push({
                    id: uuidv4(),
                    start: partStart.toISOString(),
                    end: partEnd.toISOString(),
                    title: formatEventTitle(partStart, partEnd),
                    color: "blue",
                    extendedProps: {my_duty: 1, accept_user: null}
                });
            }
        }

        setEvents(newEvents);
        setCalendarKey(prev => prev + 1);
    };

    return (
        <div className="bg-gray-100">
            <h6>Dividir Plantão</h6>
            <div className="mb-3">
                <button type="button" className="btn btn-success me-2 btn-sm" onClick={() => handleSplitEvent(2)}
                        disabled={events.length !== 1}>
                    <i className="fa fa-scissors me-1"></i> Dividir 2 partes
                </button>
                <button type="button" className="btn btn-dark me-2 btn-sm" onClick={() => handleSplitEvent(3)}
                        disabled={events.length !== 1}>
                    <i className="fa fa-scissors me-1"></i> Dividir 3 partes
                </button>
            </div>

            {events.length > 0 && (
                <FullCalendar
                    key={calendarKey}
                    plugins={[timelinePlugin, interactionPlugin]}
                    initialView="timelineDay"
                    editable={false}
                    eventResizableFromStart={true}
                    events={events}
                    eventResize={handleEventResize}
                    eventClick={handleEventClick}
                    height="auto"
                    initialDate={events[0].start}
                    slotMinTime={dutyEvent.date_time.slice(11, 19)}
                    slotMaxTime={
                        new Date(dutyEvent.date_time_end).getDate() !== new Date(dutyEvent.date_time).getDate()
                            ? `${24 + parseInt(dutyEvent.date_time_end.slice(11, 13))}:00:00`
                            : dutyEvent.date_time_end.slice(11, 19)
                    }
                    headerToolbar={false}
                    slotDuration="00:30:00"
                    slotLabelFormat={{hour: "2-digit", minute: "2-digit"}}
                    nowIndicator={false}
                    allDaySlot={false}
                    locale="pt-BR"
                    eventContent={(arg) => (
                        <div
                            style={{
                                backgroundColor: arg.event.extendedProps.my_duty === 1 ? "blue" : "gray",
                                padding: "5px",
                                borderRadius: "3px",
                                border: `1px solid ${arg.event.extendedProps.my_duty === 1 ? "blue" : "gray"}`,
                            }}
                        >
                            {arg.event.title}
                        </div>
                    )}
                />
            )}

            {events.length > 0 && (
                <div className="mt-4">
                    {events.map((event, index) => (
                        <p key={index} className="mb-2">

                            {event.extendedProps.my_duty === 0 ? (
                                <div className="row">
                                    <div className="col-7">
                                        <p className="mb-1"><strong>Disponibilizado para outros usuários</strong></p>
                                        {event.extendedProps.accept_user && (
                                            <p className="text-muted mb-2">
                                                <i className="fa fa-user me-1"></i>
                                                {event.extendedProps.accept_user.label || event.extendedProps.accept_user.name}
                                                <span className="ms-1 fst-italic">(indicado)</span>
                                            </p>
                                        )}
                                    </div>

                                    <div className="col-5 d-flex justify-content-end gap-2">
                                        <button type="button"
                                                className="btn btn-dark btn-fixed-height"
                                                onClick={() => {
                                                    setEvents((prevEvents) =>
                                                        prevEvents.map((eventMap) =>
                                                            eventMap.id === event.id
                                                                ? {
                                                                    ...eventMap,
                                                                    color: "gray",
                                                                    extendedProps: {...eventMap.extendedProps, my_duty: 1,    accept_user: null}
                                                                }
                                                                : eventMap
                                                        )
                                                    );
                                                }}
                                        >
                                            <i className="fa fa-handshake-o me-1"></i> Vou Realizar
                                        </button>

                                        {!event.showUserSearch ? (
                                            <button
                                                type="button"
                                                className="btn btn-success btn-fixed-height"
                                                onClick={() => {
                                                    setSelectedEventIndex(index);
                                                    setShowUserModal(true);
                                                }}
                                            >
                                                <i className="fa fa-user-plus me-1"></i> Indicar usuário
                                            </button>
                                        ) : null}

                                        <button
                                            type="button"
                                            onClick={() => handleEventDelete(event)}
                                            disabled={events.length === 1}
                                            className="btn btn-danger btn-fixed-height"
                                        >
                                            <i className="fa fa-trash" aria-hidden="true"></i> Excluir
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="row">
                                    <div className="col-7">
                                        <strong>
                                            <strong>{event.title}</strong>{" "} {" - "} {dutyEvent?.accept_user?.name || "Nome não disponível"}
                                        </strong>
                                    </div>
                                    <div className="col-5 d-flex justify-content-end gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-info me-2 btn-fixed-height"
                                            onClick={() => {
                                                setEvents((prevEvents) =>
                                                    prevEvents.map((eventMap) =>
                                                        eventMap.id === event.id
                                                            ? {
                                                                ...eventMap,
                                                                color: "gray",
                                                                extendedProps: {...eventMap.extendedProps, my_duty: 0}
                                                            }
                                                            : eventMap
                                                    )
                                                );
                                            }}
                                        >
                                            <i className="fa fa-exchange me-1"></i> Disponibilizar p/ Usuários
                                        </button>
                                        <button type="button" onClick={() => handleEventDelete(event)}
                                                disabled={events.length == 1}
                                                className="btn btn-danger btn-fixed-height">
                                            <i className="fa fa-trash" aria-hidden="true"></i> Excluir
                                        </button>
                                    </div>
                                </div>
                            )}
                            <hr/>
                        </p>
                    ))}
                </div>
            )}

            <Modal show={showUserModal} onHide={() => setShowUserModal(false)} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title>Indicar Usuário</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <UsersSearch
                        onUserSelect={(selectedUser) => {
                            const updatedEvents = [...events];
                            updatedEvents[selectedEventIndex].extendedProps.accept_user = selectedUser;
                            setEvents(updatedEvents);
                            setShowUserModal(false);
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowUserModal(false)}
                    >
                        <i className="fa fa-close me-1"></i> Fechar
                    </button>
                </Modal.Footer>
            </Modal>



        </div>
    );
};

export default TimelineEditor;
